/**
 * Package #1 platform foundation types.
 *
 * These describe the governance layer that sits *underneath* Broadview Insight.
 * Nothing here changes the visible product: the Problem Record, its 11 stages,
 * evidence gates, recommendation decisions and all existing terminology
 * (including "Performance vs Target") are untouched.
 */

export type AppRole =
  | "admin"
  | "quality_director"
  | "quality_engineer"
  | "supplier_quality"
  | "manufacturing_engineer"
  | "viewer";

export const ROLE_LABELS: Record<AppRole, string> = {
  admin: "System Administrator",
  quality_director: "Quality Director",
  quality_engineer: "Quality Engineer",
  supplier_quality: "Supplier Quality Engineer",
  manufacturing_engineer: "Manufacturing Engineer",
  viewer: "Viewer",
};

/** Capabilities are derived from roles; no capability is stored client side. */
export type Permission =
  | "record.read"
  | "record.edit"
  | "evidence.attach"
  | "stage.complete"
  | "recommendation.decide"
  | "escalation.raise"
  | "policy.manage"
  | "roles.manage"
  | "integration.manage";

const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  admin: [
    "record.read",
    "record.edit",
    "evidence.attach",
    "stage.complete",
    "recommendation.decide",
    "escalation.raise",
    "policy.manage",
    "roles.manage",
    "roles.manage",
    "integration.manage",
  ],
  quality_director: [
    "record.read",
    "record.edit",
    "evidence.attach",
    "stage.complete",
    "recommendation.decide",
    "escalation.raise",
    "policy.manage",
  ],
  quality_engineer: [
    "record.read",
    "record.edit",
    "evidence.attach",
    "stage.complete",
    "recommendation.decide",
    "escalation.raise",
  ],
  supplier_quality: ["record.read", "evidence.attach", "escalation.raise"],
  manufacturing_engineer: ["record.read", "evidence.attach", "escalation.raise"],
  viewer: ["record.read"],
};

export function permissionsForRoles(roles: AppRole[]): Permission[] {
  const set = new Set<Permission>();
  for (const role of roles) for (const p of ROLE_PERMISSIONS[role] ?? []) set.add(p);
  return [...set];
}

export type AuditEventType =
  | "stage.completed"
  | "evidence.attached"
  | "note.added"
  | "pfmea.updated"
  | "eightd.completed"
  | "eightd.due_date_changed"
  | "recommendation.decided"
  | "demo.reset"
  | "escalation.raised"
  | "signal.dispositioned"
  | "signal.severity_assessed"
  | "compliance.verdict_changed"
  | "financial.carry_factor_changed"
  | "financial.carry_factor_default_changed"
  | "record.created_from_signal"
  | "record.signal_linked"
  | "user_role.changed"
  | "sample.reset";

export interface AuditEventInput {
  entityType?: string;
  entityId: string;
  recordCode?: string;
  stageId?: string;
  eventType: AuditEventType;
  action: string;
  clause?: string;
  detail?: string;
  actorLabel?: string;
  decisionVerdict?: string;
  recommendationId?: string;
  authorityRuleKey?: string;
  payload?: Record<string, unknown>;
}

export interface EvidenceGovernanceInput {
  recordId: string;
  recordCode?: string;
  stageId?: string;
  chapter?: "workflow" | "8d";
  evidenceRef: string;
  name: string;
  evidenceType?: string;
  owner?: string;
  sourceSystem?: string;
  sourceReference?: string;
  evidenceDate?: string;
  version?: string;
  status?: "attached" | "under-review" | "approved" | "superseded";
  provenance?: string;
  summary?: string;
}

export type ReadinessStatus = "ready" | "caution" | "not-ready" | "unknown";

export interface ReadinessAssessment {
  score: number;
  status: ReadinessStatus;
  gateSatisfied: boolean;
  evidenceCount: number;
  requiredEvidenceCount: number;
  findings: string[];
}
